import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import { ReactComponent as AddUserIcon } from "../../assets/Icons/user-add-plus.svg";
import { ReactComponent as ShareIcon } from "../../assets/Icons/share-transmit.svg";
import { ReactComponent as SendIcon } from "../../assets/Icons/send-filled.svg";
import { ReactComponent as CoffeeCup } from "../../assets/Icons/coffee-cup.svg";
import { ERROR, SUCCESS, treatment_program } from "../../constants/common";
import { useNetwork } from "../../contexts/NetWork";
import paths from "../../constants/paths";
import { useAuth } from "../../contexts/Auth";
import { ReactComponent as MinusUserIcon } from "../../assets/Icons/user-minus.svg";
import { useLocation, useParams } from "react-router-dom";
import useResize from "../../hooks/useResize";
import useFilters from "../../hooks/useFilter";
import { trackClickEvent } from "../../mixpanel";
import { useBasicInfo } from "../../contexts/BasicInfo";
import { useGeneralProfile } from "../../contexts/Organization";
import { Tooltip } from "@mui/material";

const UserCard = ({
  data,
  onReferralClick,
  onShareClick = null,
  handleOpenOtherConnection = null,
  enableShare = true,
  enableReferral = true,
  enableRemove = true,
  enableConnections = true,
  params = null,
}) => {
  const { connectWithOthers, connectStatus, disconnectWithOthers } =
    useNetwork();
  const [loading, setLoading] = useState(false);
  const { data: infoBasic } = useBasicInfo();
  const { facilityInfo } = useGeneralProfile();
  const { account_type, permissions } = useAuth();
  const userData = data?.connected_user || data;
  const isMobileView = useResize(600);
  const location = useLocation();
  const { pathname } = location;

  const { id } = useParams();
  const info = account_type === treatment_program ? facilityInfo : infoBasic;
  const [filters] = useFilters();

  useEffect(() => {
    if ([SUCCESS, ERROR].includes(connectStatus) && Boolean(data?.is_connected))
      setLoading(false);
  }, [connectStatus, data?.is_connected]);

  const connect = async () => {
    setLoading(true);
    await connectWithOthers({
      connected_user_id:
        (filters?.provider || "therapists") === treatment_program ||
        userData.is_treatment
          ? userData.treatment_program_info?.id
          : userData.id,
      connected_user_type:
        (filters?.provider || "therapists") === treatment_program ||
        userData.is_treatment
          ? treatment_program
          : "user",
      account_type:
        account_type === treatment_program ? treatment_program : "user",
    });
    trackClickEvent("Trusted Relationship Made", {
      page: pathname,
      component: "User card",
      button: "connect",
    });
  };

  const disconnect = async () => {
    setLoading(true);
    try {
      await disconnectWithOthers({
        id:
          (filters?.provider || "therapists") === treatment_program ||
          userData.is_treatment
            ? userData.treatment_program_info?.id
            : userData.id,
        connectedUserType:
          (filters?.provider || "therapists") === treatment_program ||
          userData.is_treatment
            ? treatment_program
            : "user",
        userType:
          account_type === treatment_program ? treatment_program : "user",
      });
      setLoading(false);
      trackClickEvent("Trusted Relationship Removed", {
        page: pathname,
        component: "User card",
        button: "disconnect",
      });
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const isSameUser = () => {
    return String(userData.id) === String(localStorage.getItem("userId"));
  };

  const handleReferralClick = () => {
    trackClickEvent("Send Referral", {
      button: "Send Referral",
      page: pathname,
      component: "User Card",
    });
    onReferralClick(data);
  };

  const handleShareClick = () => {
    trackClickEvent("Share User", {
      button: "Share",
      page: pathname,
      component: "User Card",
    });
    onShareClick(data);
  };

  const isTreatment = userData?.account_type === treatment_program;
  const filteredData =
    isTreatment &&
    ((filters?.provider || "therapists") === treatment_program ||
      userData.is_treatment)
      ? userData?.treatment_program_info
      : userData?.info;
  const age_served = filteredData?.age_served_currently;
  const address = filteredData?.address;
  const currentStatus = filteredData?.current_status;
  const currentlyServed =
    isTreatment &&
    ((filters?.provider || "therapists") === treatment_program ||
      userData.is_treatment)
      ? userData?.treatment_program_info?.currently_served
      : userData.currently_served;
  const cardImage =
    isTreatment &&
    ((filters?.provider || "therapists") === treatment_program ||
      userData.is_treatment)
      ? userData?.treatment_program_info?.facility_image
      : userData.main_image;
  const contentStyleDesktop = {
    flexDirection: isMobileView ? "row" : "column",
    display: "flex",
  };
  const displayName =
    isTreatment &&
    ((filters?.provider || "therapists") === "treatment_program" ||
      userData.is_treatment)
      ? userData.treatment_program_info?.facility_name
      : `${userData?.first_name} ${userData?.last_name}${
          (filters?.provider || "therapists") !== treatment_program &&
          JSON.parse(userData?.user_degree).length
            ? ", " +
              JSON.parse(userData?.user_degree)
                .map((value) => value.degree)
                ?.join(", ")
            : ""
        }`;
  const displayAvailability =
    ((filters?.provider || "therapists") === treatment_program ||
    userData.is_treatment
      ? userData?.treatment_program_info?.send_ref
      : userData?.send_ref) && userData.user_referral;
  const openNetworking =
    (filters?.provider || "therapists") === treatment_program
      ? Boolean(userData?.treatment_program_info?.open_networking)
      : Boolean(userData?.info?.open_networking);

  const contentStyleMobile = {
    flexDirection: isMobileView ? "row" : "column",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: "12px",
    width: "100%",
  };

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: isMobileView ? 2.25 : 1,
        p: isMobileView ? 0 : 1,
        borderRadius: 3,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "center", sm: "flex-start" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          minWidth: "calc(100% - 156px)",
          maxWidth: { md: "calc(100%)" },
          flexBasis: { md: "calc(100%)" },
        }}
      >
        <Box position="relative" display="inline-block" textAlign="center">
          <Avatar
            sx={{
              width: 180,
              height: 180,
              borderRadius: 4,
            }}
            src={cardImage}
          />
          {
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "40%",
                background: `linear-gradient(to top, ${
                  displayAvailability ? "green" : "red"
                }, transparent)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                  mt: 3,
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  textShadow: "0px 0px 5px rgba(0, 0, 0, 0.5)",
                }}
              >
                {displayAvailability ? "Accepting Clients" : "Fully Booked"}
              </Typography>
            </Box>
          }
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            width: "100%",
            height: "100%",
          }}
        >
          <Box sx={!isMobileView ? contentStyleDesktop : contentStyleMobile}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Box>
                <Link
                  href={paths.userProfile(
                    userData?.uuid,
                    userData?.is_treatment
                      ? userData?.treatment_program_info?.id
                      : "",
                    (filters?.provider || "therapists") === treatment_program ||
                      userData.is_treatment
                      ? treatment_program
                      : "user",
                  )}
                  onClick={() => {
                    trackClickEvent("Profile Viewed", {
                      page: "Network",
                      component: "UserCard",
                      button: "View Profile",
                      URL: pathname,
                    });
                  }}
                  variant="h5"
                  color="text.primary"
                  textTransform="capitalize"
                  fontWeight={700}
                  sx={{
                    fontSize: "18px",
                    display: "block",
                    lineHeight: "22px",
                  }}
                >
                  <Box
                    sx={{
                      p: 0,
                      display: "inline-block",
                      maxWidth: "250px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      verticalAlign: "middle",
                    }}
                  >
                    {displayName}
                  </Box>
                </Link>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "#61676B",
                  lineHeight: "22px",
                  fontSize: "16px",
                  whiteSpace: "nowrap",
                }}
              >
                {address?.city && `${address?.city}`}
                {address?.state?.name && `, ${address?.state?.abbreviation}`}
              </Typography>
            </Box>
            <Box>
              {(userData?.account_type === treatment_program &&
                (filters?.provider || "therapists") === "therapists") ||
              data?.is_treatment === false ? (
                <Box>
                  <Link
                    href={paths.userProfile(
                      userData?.uuid,
                      userData?.treatment_program_info?.id || "",
                      treatment_program,
                    )}
                    onClick={() => {
                      trackClickEvent("Clicked Affiliation Link", {
                        page: "Network",
                        component: "User Card",
                      });
                    }}
                    variant="h5"
                    color="text.secondary"
                    fontWeight={400}
                    sx={{
                      color: "#0C8CE9",
                      fontSize: "16px",
                      fontWeight: 700,
                      display: "block",
                      lineHeight: "18px",
                      mt: 0.75,
                      mb: 1,
                    }}
                  >
                    {userData.treatment_program_info?.facility_name}
                  </Link>
                </Box>
              ) : null}
            </Box>
          </Box>
          <Box>
            <Box>
              {(currentlyServed?.length > 0 ||
                age_served?.length > 0 ||
                Boolean(
                  userData?.treatment_program_info?.treatment_type?.name,
                )) &&
              displayAvailability ? (
                <>
                  {
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.5,
                        mt: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {currentlyServed?.length > 0 && (
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: "1",
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                color: "#000000",
                                fontSize: "16px",
                                fontWeight: 700,
                                lineHeight: "18px",
                              }}
                            >
                              Currently Accepting:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: "1",
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                color: "#61676B",
                                fontSize: "16px",
                                fontWeight: 400,
                                lineHeight: "100%",
                                maxWidth: "400px",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {currentlyServed
                                ?.map((item) => item?.name)
                                .join(", ")}
                            </Typography>
                          </>
                        )}
                        {age_served?.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: "1",
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                color: "#61676B",
                                fontSize: "16px",
                                fontWeight: 400,
                                lineHeight: "100%",
                              }}
                            >
                              {age_served
                                ?.split(",")
                                .filter((item) => Boolean(item))
                                ?.join(", ")}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  }
                </>
              ) : (
                <Box minHeight={44} />
              )}
              {openNetworking ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#FF8C001A",
                    borderRadius: "20px",
                    padding: "4px 12px",
                    width: "fit-content",
                  }}
                >
                  <Box
                    sx={{
                      px: 1,
                      py: 0.625,
                      color: "#FF8C00",
                    }}
                  >
                    <CoffeeCup stroke="#FF8C00" />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#000",
                      fontSize: "14px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      pr: 1,
                    }}
                  >
                    Open to Networking
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row", md: "column" },
          justifyContent: { xs: "center", sm: "space-between" },
          gap: 2,
          minWidth: 140,
          maxWidth: { md: 140 },
          flexBasis: { md: 140 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "start",
            justifyContent: "flex-end",
          }}
        >
          {isSameUser() ? null : !data.is_connected &&
            !Boolean(data?.connected_user) ? (
            <IconButton
              onClick={connect}
              disabled={loading || !permissions?.network_write}
              sx={{
                maxWidth: { sm: 150, md: "none" },
                color: "primary.main",
                py: 0,
              }}
            >
              <AddUserIcon width={22} height={22} />
            </IconButton>
          ) : (
            !(pathname === paths?.network("all")) &&
            enableRemove && (
              <IconButton
                onClick={disconnect}
                disabled={loading || !permissions?.network_write}
                sx={{
                  maxWidth: { sm: 150, md: "none" },
                  color: "primary.main",
                  py: 0,
                }}
              >
                <MinusUserIcon
                  color="red"
                  style={{ color: "red !important" }}
                  width={22}
                  height={22}
                />
              </IconButton>
            )
          )}
          {/* handleShareClick calls onShareClick(data) unguarded, and onShareClick
              defaults to null -- call sites that omit it (e.g. ConnectionsModal)
              rendered a share button that threw "onShareClick is not a function".
              Only render it when a handler was actually supplied. */}
          {!onShareClick || (enableShare && isMobileView) ? null : (
            <IconButton
              disabled={loading || !permissions?.network_share}
              sx={{
                maxWidth: { sm: 150, md: "none" },
                color: "primary.main",
                py: 0,
              }}
              onClick={handleShareClick}
            >
              <ShareIcon width={22} height={22} />
            </IconButton>
          )}
        </Box>
        <Box
          sx={{
            textAlign: "end",
            gap: "8px",
          }}
        >
          {enableConnections && (
            <Typography
              variant="contained"
              onClick={() =>
                handleOpenOtherConnection && handleOpenOtherConnection(data)
              }
              color={"#0C8CE9"}
              fontWeight={700}
              fontSize={"16px"}
              sx={{
                pr: 0.5,
                whiteSpace: "nowrap",
                maxWidth: { sm: 140, md: "none" },
                flex: 1,
                cursor: "pointer",
              }}
            >
              &nbsp;&nbsp;Who they trust
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {
            <Tooltip
              title={
                !displayAvailability
                  ? "Not accepting new referrals. Please check back later."
                  : ""
              }
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: ".8rem",
                    marginBottom: "12px",
                  },
                },
              }}
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleReferralClick}
                  startIcon={<SendIcon />}
                  sx={{
                    py: 0.75,
                    whiteSpace: "nowrap",
                    maxWidth: { sm: 140, md: "none" },
                    flex: 1,
                  }}
                  disabled={
                    !displayAvailability ||
                    !permissions?.connection_send_referral
                  }
                >
                  &nbsp;&nbsp;Send Referral
                </Button>
              </span>
            </Tooltip>
          }
        </Box>
      </Box>
    </Paper>
  );
};

export default UserCard;
